import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import type { BillingLedger } from '../../../contexts/BillingContext';
import { updateBillingLedger } from '../../../services/billingService';
import { httpClient } from '../../../services/http';
import type { PricingSettings } from '../../../shared/constants/pricing';

interface CreditsAndUsageProps {
  clientId: string;
  ledger: BillingLedger | null;
  pricingSettings: PricingSettings;
  billingCurrency: string;
  totalUnbilledUsageCost: number;
  enableStripeBilling?: boolean;
  cycleEndDate: Date | null;
}

export const CreditsAndUsage: React.FC<CreditsAndUsageProps> = ({
  clientId,
  ledger,
  pricingSettings,
  billingCurrency,
  totalUnbilledUsageCost,
  enableStripeBilling,
  cycleEndDate,
}) => {
  const theme = useTheme();
  const [loadingBuy, setLoadingBuy] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const [editLimitValue, setEditLimitValue] = useState<string>('');
  const [savingLimit, setSavingLimit] = useState(false);

  const costPerCredit = pricingSettings.cost_per_credit_in_base || 1.0;

  const handleBuyCredits = async () => {
    setLoadingBuy(true);
    setError(null);
    try {
      const checkoutRes = await httpClient.post<{ result: { url: string } }>(
        '/createCheckoutSession',
        {
          isPreload: true,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
        }
      );

      const { url } = checkoutRes.result;
      window.location.href = url;
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      // biome-ignore lint/suspicious/noConsole: Expected for error handling
      console.error(error);
      setError(
        error.response?.data?.error ||
          error.message ||
          'An error occurred while connecting to billing.'
      );
    } finally {
      setLoadingBuy(false);
    }
  };

  const handleManageBilling = async () => {
    setLoadingPortal(true);
    setError(null);
    try {
      const res = await httpClient.post<{ result: { url: string } }>(
        '/createCustomerPortalSession',
        {
          returnUrl: window.location.href,
        }
      );

      const { url } = res.result;
      window.location.href = url;
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } }; message?: string };
      // biome-ignore lint/suspicious/noConsole: Expected for error handling
      console.error(error);
      setError(
        error.response?.data?.error ||
          error.message ||
          'An error occurred while opening the billing portal.'
      );
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleSaveLimit = async () => {
    setSavingLimit(true);
    setError(null);
    try {
      // User inputs in currency, save in credits
      const newLimitCurrency = editLimitValue === '' ? undefined : parseFloat(editLimitValue);
      const newLimitCredits =
        newLimitCurrency !== undefined ? Math.floor(newLimitCurrency / costPerCredit) : undefined;
      const payload: Partial<BillingLedger> = {};
      if (newLimitCredits !== undefined) {
        payload.spending_limit = newLimitCredits;
      }
      await updateBillingLedger(clientId, payload);
      setIsEditingLimit(false);
    } catch (err) {
      // biome-ignore lint/suspicious/noConsole: Expected for error handling
      console.error(err);
      setError('Failed to update spending limit.');
    } finally {
      setSavingLimit(false);
    }
  };

  const currentBalanceAud = (ledger?.balance || 0) * costPerCredit;
  const spendingLimitAud =
    ledger?.spending_limit !== undefined ? ledger.spending_limit * costPerCredit : undefined;

  return (
    <Paper sx={{ p: 3, backgroundColor: theme.palette.greyLight, height: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Credits and Usage
      </Typography>

      <Grid container spacing={2} sx={{ my: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, height: '100%' }}>
            <Typography variant="subtitle1" color="text.secondary">
              Balance (pre-paid)
            </Typography>
            <Typography
              variant="h4"
              color={
                ledger?.balance === 0
                  ? theme.palette.mode === 'dark'
                    ? 'error.light'
                    : 'error.main'
                  : theme.palette.mode === 'dark'
                    ? 'primary.light'
                    : 'primary.main'
              }
            >
              $
              {currentBalanceAud.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {billingCurrency}
            </Typography>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, height: '100%' }}>
            <Typography variant="subtitle1" color="text.secondary">
              Usage (Unbilled)
            </Typography>
            <Typography variant="h4">
              $
              {totalUnbilledUsageCost.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              {billingCurrency}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {ledger?.discount ? (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'success.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <Typography variant="body1" color="success.dark" fontWeight="bold">
            Active Affiliate Discount: {ledger.discount}% Off Generative Usage
          </Typography>
          {!!ledger.discount_expires_at && (
            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
              Expires on:{' '}
              {new Date(
                typeof (ledger.discount_expires_at as { toDate?: () => Date }).toDate === 'function'
                  ? (ledger.discount_expires_at as { toDate: () => Date }).toDate()
                  : (ledger.discount_expires_at as string | number)
              ).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      ) : null}

      {ledger?.isPAYG && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Spending limit (ending {cycleEndDate ? cycleEndDate.toLocaleDateString() : 'N/A'}):
          </Typography>

          {isEditingLimit ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                placeholder="No Limit"
                value={editLimitValue}
                onChange={(e) => setEditLimitValue(e.target.value)}
                disabled={savingLimit}
                sx={{ width: 120 }}
                inputProps={{ step: '0.01' }}
              />
              <Typography variant="body2" color="text.secondary">
                {billingCurrency}
              </Typography>
              <IconButton
                size="small"
                onClick={handleSaveLimit}
                disabled={savingLimit}
                color="primary"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsEditingLimit(false)}
                disabled={savingLimit}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                {spendingLimitAud !== undefined
                  ? `$${spendingLimitAud.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${billingCurrency}`
                  : 'No Limit'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setEditLimitValue(spendingLimitAud !== undefined ? String(spendingLimitAud) : '');
                  setIsEditingLimit(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!enableStripeBilling ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please contact your administrator or BIDLOGIQ support to purchase additional credits.
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: theme.palette.bidlogiq?.brick || '#B22222',
                color: '#fff',
                '&:hover': {
                  backgroundColor: theme.palette.bidlogiq?.brick || '#B22222',
                  filter: 'brightness(0.9)',
                },
              }}
              onClick={handleBuyCredits}
              disabled={loadingBuy || loadingPortal}
              startIcon={loadingBuy ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              Buy Credits (10% off)
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleManageBilling}
              disabled={loadingBuy || loadingPortal}
              startIcon={loadingPortal ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              Manage Billing
            </Button>
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant={enableStripeBilling ? 'text' : 'contained'}
          color="primary"
          onClick={() => (window.location.href = 'mailto:support@bidlogiq.ai')}
        >
          Contact Support
        </Button>
      </Box>
    </Paper>
  );
};
